<template>
  <div class="student-homework-summary">
    <!-- 作业汇总头部 -->
    <div class="summary-header">
      <h3><i class="el-icon-document"></i> 作业汇总</h3>
      <!-- 调试信息显示 -->
      <div class="debug-info" v-if="debugInfo">
        <el-tag type="info" size="small">用户ID: {{ debugInfo.userId }}</el-tag>
        <el-tag type="success" size="small">学生ID: {{ debugInfo.studentId || '未绑定' }}</el-tag>
        <el-tag type="warning" size="small">班级ID: {{ debugInfo.classIds || '未注册' }}</el-tag>
        <el-tag type="primary" size="small">课程ID: {{ courseId }}</el-tag>
      </div>
    </div>

    <!-- 无数据时的默认显示 -->
    <el-card v-if="showNoDataMessage" class="empty-homework-card">
      <div slot="header" class="homework-header">
        <span><i class="el-icon-document"></i> 作业汇总</span>
      </div>
      <div class="empty-content">
        <div class="empty-icon">
          <i class="el-icon-warning"></i>
        </div>
        <div class="empty-text">
          <h3>{{ noDataMessage }}</h3>
          <p>请先选择课程后再查看作业</p>
        </div>
        <div class="empty-actions">
          <el-button type="primary" size="large" @click="goToCourseSelection">
            <i class="el-icon-edit"></i> 去选课
          </el-button>
        </div>
      </div>
    </el-card>

    <!-- 添加标签页 -->
    <el-tabs v-model="activeTab" type="border-card" style="margin-top: 20px;" v-if="!showNoDataMessage">
      <el-tab-pane label="未提交作业" name="unsubmitted">
        <el-table
          v-loading="loading"
          :data="unsubmittedHomework"
          border
          style="width: 100%"
          empty-text="暂无未提交的作业">
          <el-table-column prop="id" label="ID" width="80" align="center" />
          <el-table-column prop="name" label="作业名称" align="center">
            <template slot-scope="scope">
              <el-button type="text" @click="handleViewHomework(scope.row)">{{ scope.row.name }}</el-button>
            </template>
          </el-table-column>
          <el-table-column label="状态" align="center" width="150">
            <template>
              <el-tag type="info">未提交</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" align="center" width="400">
            <template slot-scope="scope">
              <el-button type="text" size="small" @click="handleView(scope.row)">查看</el-button>
              <el-button type="text" size="small" @click="handleDownload(scope.row)">下载</el-button>
              <el-button type="text" size="small" @click="handleUpload(scope.row)">上传作业图片</el-button>
              <el-button type="text" size="small" @click="handleOnlineExam(scope.row)">在线答题</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>

      <el-tab-pane label="已提交作业" name="submitted">
        <el-table
          v-loading="loading"
          :data="submittedHomework"
          border
          style="width: 100%"
          empty-text="暂无已提交的作业">
          <el-table-column prop="id" label="ID" width="80" align="center" />
          <el-table-column prop="name" label="作业名称" align="center">
            <template slot-scope="scope">
              <el-button type="text" @click="handleViewHomework(scope.row)">{{ scope.row.name }}</el-button>
            </template>
          </el-table-column>
          <el-table-column label="状态" align="center" width="150">
            <template slot-scope="scope">
              <el-tag :type="getGradingStatusType(scope.row.status)">
                {{ getGradingStatusText(scope.row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="分数" align="center" width="100">
            <template slot-scope="scope">
              <span v-if="getSubmissionPoint(scope.row) !== null">{{ getSubmissionPoint(scope.row) }}</span>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" align="center" width="250">
            <template slot-scope="scope">
              <!-- 只有未批改的作业才显示操作按钮 -->
              <template v-if="scope.row.status === '未批改'">
                <el-button
                  type="text"
                  size="small"
                  @click="handleReuploadImages(scope.row)"
                  style="color: #409eff;"
                >
                  重新上传作业图片
                </el-button>
                <el-button
                  type="text"
                  size="small"
                  @click="handleOnlineExam(scope.row)"
                  style="color: #67c23a;"
                >
                  在线答题
                </el-button>
              </template>
              <!-- 已批改的作业显示批改完成状态 -->
              <span v-if="scope.row.status === '已批改'" style="color: #67c23a; font-size: 12px;">
                已批改
              </span>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>
    </el-tabs>

    <!-- 作业详情弹窗 -->
    <el-dialog title="作业详情" :visible.sync="dialogVisible" width="90%" :before-close="handleDialogClose" @opened="handleDialogOpened" @closed="handleDialogClosed">
      <!-- 未提交作业的简化显示 -->
      <div v-if="currentHomework && !currentHomework.submitted" style="padding: 20px;">
        <h3 style="margin-bottom: 20px; color: #333;">题目列表</h3>
        <el-table :data="homeworkDetail" border style="width: 100%">
          <el-table-column label="序号" width="80" align="center">
            <template slot-scope="scope">
              {{ scope.$index + 1 }}
            </template>
          </el-table-column>
          <el-table-column prop="content" label="题目内容" align="left" min-width="300">
            <template slot-scope="scope">
              <div style="max-height: 100px; overflow-y: auto; line-height: 1.5;">
                {{ scope.row.content }}
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="quType" label="题目类型" align="center" width="120">
            <template slot-scope="scope">
              <el-tag size="small" type="info">
                <span v-if="scope.row.quType === 1 || scope.row.quType === '1'">选择题</span>
                <span v-else-if="scope.row.quType === 2 || scope.row.quType === '2'">填空题</span>
                <span v-else-if="scope.row.quType === 3 || scope.row.quType === '3'">判断题</span>
                <span v-else-if="scope.row.quType === 4 || scope.row.quType === '4'">简答题</span>
                <span v-else>未知类型({{ scope.row.quType }})</span>
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="point" label="分值" align="center" width="80">
            <template slot-scope="scope">
              {{ scope.row.point || 0 }}分
            </template>
          </el-table-column>
          <el-table-column prop="difficulty" label="难度" align="center" width="100">
            <template slot-scope="scope">
              <el-rate
                :value="scope.row.difficulty || 0"
                disabled
                show-score
                text-color="#ff9900"
                score-template="{value}"
                style="font-size: 12px;"
              />
            </template>
          </el-table-column>
        </el-table>

        <div style="margin-top: 20px; text-align: center;">
          <el-button type="primary" icon="el-icon-download" @click="handleDownload(currentHomework)">
            下载作业题目
          </el-button>
        </div>
      </div>

      <!-- 已提交作业的详细显示 -->
      <div v-else-if="hasHomeworkImages" style="display: flex; height: 600px;">
        <!-- 左侧：作业图片预览区域 -->
        <div style="flex: 1; border-right: 1px solid #eee; padding-right: 20px; display: flex; flex-direction: column;">
          <h3 style="margin-bottom: 15px;">作业图片预览</h3>

          <!-- 图片显示区域 -->
          <div style="flex: 1; display: flex; justify-content: center; align-items: center; background: #f5f5f5; border-radius: 8px; margin-bottom: 15px; position: relative; min-height: 400px; border: 2px dashed #ddd;">
            <img
              v-if="homeworkImages.length > 0"
              :src="homeworkImages[currentImageIndex]"
              alt="作业图片"
              style="max-width: 100%; max-height: 100%; object-fit: contain; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); cursor: pointer;"
              @error="handleImageError"
              @click="openImageInNewTab"
            />
            <div v-else style="color: #999; text-align: center;">
              <i class="el-icon-picture" style="font-size: 48px; margin-bottom: 10px;"></i>
              <p>暂无图片</p>
            </div>

            <!-- 图片加载提示 -->
            <div v-if="homeworkImages.length > 0" style="position: absolute; top: 10px; right: 10px; background: rgba(0,0,0,0.7); color: white; padding: 4px 8px; border-radius: 4px; font-size: 12px;">
              {{ currentImageIndex + 1 }} / {{ homeworkImages.length }}
            </div>
          </div>

          <!-- 图片控制按钮 -->
          <div v-if="homeworkImages.length > 1" style="display: flex; justify-content: center; align-items: center; gap: 10px;">
            <el-button
              type="primary"
              icon="el-icon-arrow-left"
              :disabled="currentImageIndex === 0"
              @click="prevImage"
              size="small"
            >
              上一张
            </el-button>
            <span style="color: #666; font-size: 14px;">
              {{ currentImageIndex + 1 }} / {{ homeworkImages.length }}
            </span>
            <el-button
              type="primary"
              icon="el-icon-arrow-right"
              :disabled="currentImageIndex === homeworkImages.length - 1"
              @click="nextImage"
              size="small"
            >
              下一张
            </el-button>
          </div>

          <!-- 图片缩略图列表 -->
          <div v-if="homeworkImages.length > 1" style="display: flex; gap: 8px; overflow-x: auto; padding: 10px 0;">
            <div
              v-for="(image, index) in homeworkImages"
              :key="index"
              @click="selectImage(index)"
              :style="{
                width: '60px',
                height: '60px',
                border: currentImageIndex === index ? '2px solid #409eff' : '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer',
                overflow: 'hidden',
                flexShrink: 0
              }"
            >
              <img
                :src="image"
                alt="缩略图"
                style="width: 100%; height: 100%; object-fit: cover;"
                @error="handleImageError"
              />
            </div>
          </div>
        </div>

        <!-- 右侧：作答详情区域 -->
        <div style="flex: 1; padding-left: 20px; overflow-y: auto;">
          <h3 style="margin-bottom: 15px;">作答详情</h3>

          <div v-if="answerDetails && answerDetails.length > 0">
            <div v-for="(detail, index) in answerDetails" :key="index" style="margin-bottom: 20px; padding: 15px; border: 1px solid #eee; border-radius: 8px; background: #fafafa;">
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
                <strong style="font-size: 16px;">第{{ getQuestionIndex(detail.questionId) + 1 }}题</strong>
                <el-tag size="small" type="info">{{ getQuestionTypeName(detail.questionType) }}</el-tag>
              </div>

              <!-- 题目内容 -->
              <div style="margin-bottom: 12px; color: #666; font-size: 14px; line-height: 1.6;">
                {{ getQuestionContent(detail.questionId) }}
              </div>

              <!-- 文字答案 -->
              <div v-if="detail.answerText" style="margin-bottom: 12px;">
                <strong style="color: #333;">文字答案：</strong>
                <div style="margin-top: 5px; padding: 8px; background: #e8f4fd; border-radius: 4px; color: #409eff;">
                  {{ detail.answerText }}
                </div>
              </div>

              <!-- 图片答案 -->
              <div v-if="detail.answerImageUrl" style="margin-bottom: 12px;">
                <strong style="color: #333;">图片答案：</strong>
                <div style="margin-top: 5px;">
                  <img :src="detail.answerImageUrl" alt="题目图片答案" style="max-width: 200px; max-height: 150px; border-radius: 4px; border: 1px solid #ddd;" @error="handleImageError" />
                </div>
              </div>

              <!-- 未作答提示 -->
              <div v-if="!detail.answerText && !detail.answerImageUrl" style="margin-bottom: 12px;">
                <strong style="color: #333;">作答情况：</strong>
                <span style="color: #f56c6c; margin-left: 5px;">未作答</span>
              </div>

              <!-- 得分情况 -->
              <div v-if="detail.studentScore !== undefined" style="margin-bottom: 12px;">
                <strong style="color: #333;">得分：</strong>
                <span :style="{ color: detail.studentScore > 0 ? '#67c23a' : '#f56c6c', fontWeight: 'bold', marginLeft: '5px' }">
                  {{ detail.studentScore }}/{{ detail.questionScore }}
                </span>
              </div>

              <!-- 教师评语 -->
              <div v-if="detail.teacherComment" style="margin-bottom: 12px;">
                <strong style="color: #333;">教师评语：</strong>
                <div style="margin-top: 5px; padding: 8px; background: #fdf6ec; border-radius: 4px; color: #e6a23c;">
                  {{ detail.teacherComment }}
                </div>
              </div>

              <!-- 批改状态 -->
              <div v-if="detail.isCorrect !== undefined">
                <strong style="color: #333;">批改状态：</strong>
                <el-tag
                  size="small"
                  :type="detail.isCorrect === 1 ? 'success' : (detail.isCorrect === 0 ? 'danger' : 'info')"
                  style="margin-left: 5px;"
                >
                  {{ detail.isCorrect === 1 ? '正确' : (detail.isCorrect === 0 ? '错误' : '未批改') }}
                </el-tag>
              </div>
            </div>
          </div>
          <div v-else style="text-align: center; color: #999; padding: 40px 0;">
            <i class="el-icon-document" style="font-size: 48px; margin-bottom: 10px;"></i>
            <p>暂无作答详情</p>
          </div>
        </div>
      </div>

      <!-- 没有图片时的传统布局 -->
      <div v-else style="display: flex;">
        <!-- 左侧：题目详情 -->
        <div style="flex: 1; border-right: 1px solid #eee; padding-right: 20px;">
          <h3 style="margin-bottom: 15px;">题目列表</h3>
          <el-table :data="homeworkDetail" border style="width: 100%">
            <el-table-column label="序号" width="80" align="center">
              <template slot-scope="scope">
                {{ scope.$index + 1 }}
              </template>
            </el-table-column>
            <el-table-column prop="content" label="题目内容" align="center" />
            <el-table-column prop="quType" label="题目类型" align="center">
              <template slot-scope="scope">
                <span v-if="scope.row.quType === 1 || scope.row.quType === '1'">选择题</span>
                <span v-else-if="scope.row.quType === 2 || scope.row.quType === '2'">填空题</span>
                <span v-else-if="scope.row.quType === 3 || scope.row.quType === '3'">判断题</span>
                <span v-else-if="scope.row.quType === 4 || scope.row.quType === '4'">简答题</span>
                <span v-else>未知类型({{ scope.row.quType }})</span>
              </template>
            </el-table-column>
            <el-table-column prop="gradeId" label="年级ID" align="center" />
          </el-table>
        </div>
        <!-- 右侧：我的作答 -->
        <div style="flex: 1; padding-left: 20px;">
          <h3 style="margin-bottom: 15px;">我的作答</h3>

          <!-- 整体作答图片 -->
          <div v-if="myAnswer && myAnswer.imageUrl" style="margin-bottom: 20px;">
            <h4>整体作答图片</h4>
            <img :src="myAnswer.imageUrl" alt="我的作答图片" style="max-width:100%;" @error="handleImageError" />
          </div>

          <!-- 作答详情 -->
          <div>
            <h4>作答详情</h4>
            <div v-if="answerDetails && answerDetails.length > 0">
              <div v-for="(detail, index) in answerDetails" :key="index" style="margin-bottom: 15px; padding: 10px; border: 1px solid #eee; border-radius: 4px;">
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
                  <strong>第{{ getQuestionIndex(detail.questionId) + 1 }}题</strong>
                  <el-tag size="mini" type="info">{{ getQuestionTypeName(detail.questionType) }}</el-tag>
                </div>

                <!-- 题目内容 -->
                <div style="margin-bottom: 8px; color: #666; font-size: 14px;">
                  {{ getQuestionContent(detail.questionId) }}
                </div>

                <!-- 文字答案 -->
                <div v-if="detail.answerText" style="margin-bottom: 8px;">
                  <strong>文字答案：</strong>
                  <span style="color: #409eff;">{{ detail.answerText }}</span>
                </div>

                <!-- 图片答案 -->
                <div v-if="detail.answerImageUrl" style="margin-bottom: 8px;">
                  <strong>图片答案：</strong>
                  <div style="margin-top: 5px;">
                    <img :src="detail.answerImageUrl" alt="题目图片答案" style="max-width: 200px; max-height: 150px; border-radius: 4px;" @error="handleImageError" />
                  </div>
                </div>

                <!-- 未作答提示 -->
                <div v-if="!detail.answerText && !detail.answerImageUrl" style="margin-bottom: 8px;">
                  <strong>作答情况：</strong>
                  <span style="color: #f56c6c;">未作答</span>
                </div>

                <!-- 得分情况 -->
                <div v-if="detail.studentScore !== undefined" style="margin-bottom: 8px;">
                  <strong>得分：</strong>
                  <span :style="{ color: detail.studentScore > 0 ? '#67c23a' : '#f56c6c' }">
                    {{ detail.studentScore }}/{{ detail.questionScore }}
                  </span>
                </div>

                <!-- 教师评语 -->
                <div v-if="detail.teacherComment" style="margin-bottom: 8px;">
                  <strong>教师评语：</strong>
                  <span style="color: #e6a23c;">{{ detail.teacherComment }}</span>
                </div>

                <!-- 批改状态 -->
                <div v-if="detail.isCorrect !== undefined">
                  <strong>批改状态：</strong>
                  <el-tag
                    size="mini"
                    :type="detail.isCorrect === 1 ? 'success' : (detail.isCorrect === 0 ? 'danger' : 'info')"
                  >
                    {{ detail.isCorrect === 1 ? '正确' : (detail.isCorrect === 0 ? '错误' : '未批改') }}
                  </el-tag>
                </div>
              </div>
            </div>
            <div v-else>
              <span style="color: #999;">暂无作答详情</span>
            </div>
          </div>
        </div>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">关 闭</el-button>
        <el-button type="primary" @click="handleDownload(currentHomework)">下 载</el-button>
      </span>
    </el-dialog>

    <!-- 上传作业弹窗 -->
    <el-dialog title="上传作业" :visible.sync="uploadDialogVisible" width="30%">
      <el-upload
        class="upload-demo"
        drag
        action=""
        :auto-upload="false"
        :on-preview="handlePreview"
        :on-remove="handleRemove"
        :before-remove="beforeRemove"
        multiple
        :limit="3"
        :on-exceed="handleExceed"
        :file-list="fileList"
        :on-change="handleFileChange">
        <i class="el-icon-upload"></i>
        <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
        <div class="el-upload__tip" slot="tip">只能上传jpg/png文件</div>
      </el-upload>
      <span slot="footer" class="dialog-footer">
        <el-button @click="uploadDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="submitUpload" :loading="uploading">确 定</el-button>
      </span>
    </el-dialog>

    <!-- 批改结果弹窗 -->
    <el-dialog title="批改结果" :visible.sync="judgeResultVisible" width="70%" v-loading="uploading" element-loading-text="正在批改中，请稍候...">
      <el-table :data="judgeResults" border style="width: 100%">
        <el-table-column prop="stem" label="题目" min-width="400">
          <template slot-scope="scope">
            {{ scope.row.stem }}
          </template>
        </el-table-column>
        <el-table-column prop="judge" label="结果" width="100" align="center">
          <template slot-scope="scope">
            <el-tag
              :type="scope.row.judge === 'right' ? 'success' : (scope.row.judge === 'error' ? 'danger' : 'info')"
            >
              <template v-if="scope.row.judge === 'right'">正确</template>
              <template v-else-if="scope.row.judge === 'error'">错误</template>
              <template v-else>未判断</template>
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="comment" label="评语" min-width="200">
          <template slot-scope="scope">
            {{ scope.row.comment }}
          </template>
        </el-table-column>
      </el-table>
      <span slot="footer" class="dialog-footer">
        <el-button @click="judgeResultVisible = false">关 闭</el-button>
      </span>
    </el-dialog>

    <!-- 作业批改结果弹窗 -->
    <el-dialog title="作业批改结果" :visible.sync="homeworkResultVisible" width="70%">
      <el-table :data="homeworkResults" border style="width: 100%">
        <el-table-column prop="stem" label="题目" min-width="400">
          <template slot-scope="scope">
            {{ scope.row.stem }}
          </template>
        </el-table-column>
        <el-table-column prop="judge" label="结果" width="100" align="center">
          <template slot-scope="scope">
            <el-tag
              :type="scope.row.judge === 'right' ? 'success' : (scope.row.judge === 'error' ? 'danger' : 'info')"
            >
              <template v-if="scope.row.judge === 'right'">正确</template>
              <template v-else-if="scope.row.judge === 'error'">错误</template>
              <template v-else>未判断</template>
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="comment" label="评语" min-width="200">
          <template slot-scope="scope">
            {{ scope.row.comment }}
          </template>
        </el-table-column>
      </el-table>
      <span slot="footer" class="dialog-footer">
        <el-button @click="homeworkResultVisible = false">关 闭</el-button>
      </span>
    </el-dialog>

    <!-- 新增：我的作业图片弹窗 -->
    <el-dialog title="我的作业图片" :visible.sync="myImageDialogVisible" width="40%">
      <img :src="myImageUrl" alt="作业图片" style="max-width:100%;display:block;margin:auto;" v-if="myImageUrl"/>
      <span slot="footer" class="dialog-footer">
        <el-button @click="myImageDialogVisible = false">关 闭</el-button>
      </span>
    </el-dialog>

    <router-view></router-view>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { getToken } from '@/utils/auth'
import { parseJwt } from '@/utils/jwtUtils'
import {
  getHomeworkList,
  getHomeworkListByCourseId,
  getHomeworkListByUserIdAndCourseId,
  addStudentSubmission,
  uploadFile,
  getHomeworkCorrectionResults,
  getQuestionDetail,
  generateExam,
  getSpecificHomeworkQuestions
} from '@/api/homework'
import { getSubmitByStudentIdAndHomeworkId, getSubmitDetails, submitHomework, getSubmitsByStudentId, returnHomework, uploadHomeworkImage, updateHomeworkImageUrl } from '@/api/homeworkSubmit'
import { getUserInfo, getStudentClassesInCourse, getStudentBindInfo } from '@/api/user'

export default {
  name: 'homework',
  props: {
    courseId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      downloaddata: {
        exam_title: '模拟考试试卷',
        student_info: {
          student_id: '',
          name: ''
        },
        question_data: [
          {
            type: '选择题',
            question: '以下哪个是正确的图标？',
            score: 5,
            options: [5, 6, 7, 8]
          },
          {
            type: '选择题',
            question: '1+1=?',
            score: 5,
            options: ['1', '2', '3', '4']
          },
          {
            type: '填空题',
            question: 'Python 中的列表用 ____ 表示。',
            score: 3
          },
          {
            type: '填空题',
            question: '2 + 3 = ____',
            score: 3
          },
          {
            type: '计算题',
            question: '计算 2 + 3 的结果。',
            score: 5
          },
          {
            type: '计算题',
            question: '计算 4 × 5 的结果。',
            score: 5
          },
          {
            type: '解答题',
            question: '简述 Python 中面向对象编程的概念。',
            score: 10
          },
          {
            type: '解答题',
            question: '解释什么是变量。',
            score: 10
          }
        ]
      },
      checkList: [],
      homeworkList: [], // 存储作业列表数据
      dialogVisible: false, // 控制弹窗显示
      homeworkDetail: [], // 存储作业详情数据
      currentHomework: null, // 当前查看的作业
      uploadDialogVisible: false, // 控制上传弹窗显示
      fileList: [], // 上传文件列表
      loading: false, // 加载状态
      selectedFiles: [], // 存储选中的文件
      totalStudents: 0, // 班级总人数
      submissions: [], // 所有提交记录
      judgeResultVisible: false, // 控制批改结果弹窗显示
      judgeResults: [], // 存储批改结果
      uploading: false, // 控制上传加载状态
      homeworkResultVisible: false, // 控制作业批改结果弹窗显示
      homeworkResults: [], // 存储作业批改结果
      myImageDialogVisible: false,
      myImageUrl: '',
      myAnswer: {}, // 存储我的作答（如图片路径等）
      answerDetails: [], // 存储作答详情
      finalErrorList: [], // 新增 finalErrorList
      activeTab: 'unsubmitted', // 当前激活的标签页
      homeworkImages: [], // 存储作业图片列表
      currentImageIndex: 0, // 当前显示的图片索引
      hasHomeworkImages: false, // 是否有作业图片
      selectedSubject: '', // 当前选中的科目
      subjectOptions: [
        { value: '数学', label: '数学' },
        { value: '语文', label: '语文' },
        { value: '英语', label: '英语' }
      ],
      showNoDataMessage: false,
      noDataMessage: '',
      debugInfo: null // 调试信息
    }
  },
  computed: {
    ...mapGetters([
      'roles'
    ]),
    // 未提交的作业列表（添加科目筛选）
    unsubmittedHomework() {
      console.log('计算未提交作业列表 - 原始作业列表:', this.homeworkList)
      let filteredList = this.homeworkList.filter(homework => homework && !homework.submitted)
      console.log('过滤后的未提交作业:', filteredList)
      if (this.selectedSubject) {
        filteredList = filteredList.filter(homework => homework && homework.subject === this.selectedSubject)
        console.log('按科目筛选后的未提交作业:', filteredList)
      }
      return filteredList
    },
    // 已提交的作业列表（添加科目筛选）
    submittedHomework() {
      console.log('计算已提交作业列表 - 原始作业列表:', this.homeworkList)
      let filteredList = this.homeworkList.filter(homework => homework && homework.submitted)
      console.log('过滤后的已提交作业:', filteredList)
      if (this.selectedSubject) {
        filteredList = filteredList.filter(homework => homework && homework.subject === this.selectedSubject)
        console.log('按科目筛选后的已提交作业:', filteredList)
      }
      return filteredList
    }
  },
  created() {
    console.log('StudentHomeworkSummary created - roles:', this.roles)
    console.log('StudentHomeworkSummary created - courseId:', this.courseId)
    console.log('StudentHomeworkSummary created - showNoDataMessage:', this.showNoDataMessage)

    // 获取调试信息
    this.getDebugInfo()

    // 检查用户角色
    if (!this.roles.includes('student')) {
      console.error('StudentHomeworkSummary - 用户角色不是student，roles:', this.roles)
      console.error('StudentHomeworkSummary - 这可能是教师用户访问了学生组件')

      // 临时保护：如果是教师用户，不跳转到404，而是显示提示
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
      console.log('StudentHomeworkSummary - 用户信息:', userInfo)
      if (userInfo.roleId === 2 || userInfo.roleId === 4) {
        console.log('StudentHomeworkSummary - 检测到教师用户，显示提示而不是跳转404')
        this.showNoDataMessage = true
        this.noDataMessage = '此功能仅限学生使用'
        console.log('StudentHomeworkSummary - 设置showNoDataMessage为true')
        return
      }

      this.$message.error('您没有权限访问此页面')
      this.$router.push('/404')
    } else {
      console.log('StudentHomeworkSummary - 用户角色检查通过，继续处理')
      // 检查courseId是否存在
      if (!this.courseId) {
        console.log('StudentHomeworkSummary - courseId不存在:', this.courseId)
        this.showNoDataMessage = true
        this.noDataMessage = '课程ID不存在'
        console.log('StudentHomeworkSummary - 设置showNoDataMessage为true，因为courseId不存在')
      } else {
        console.log('StudentHomeworkSummary - courseId存在，开始获取作业列表')
        // 获取课程作业列表数据
        this.getHomeworkList()
      }
    }
  },
  methods: {
    // 获取调试信息
    async getDebugInfo() {
      try {
        const token = getToken()
        if (!token) {
          console.warn('未获取到token')
          return
        }

        const userInfo = parseJwt(token)
        const userId = JSON.parse(userInfo.userInfo).id

        // 从localStorage获取用户信息
        const localUserInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')

        // 通过API获取用户详细信息，包括student_id
        let studentId = null
        let classIds = null
        try {
          const response = await getUserInfo()
          if (response && response.code === 1) {
            studentId = response.data.studentId
            // 使用新的API获取学生在当前课程中的班级信息
            try {
              console.log('开始获取学生在课程', this.courseId, '中的班级信息')
              const classResponse = await getStudentClassesInCourse(this.courseId)
              console.log('班级信息API响应:', classResponse)

              if (classResponse && classResponse.code === 1) {
                const classes = classResponse.data || []
                console.log('班级列表:', classes)
                classIds = classes.map(cls => cls.classId).join(', ')
                console.log('处理后的班级ID:', classIds)
                if (classIds === '') {
                  classIds = '未注册到该课程的班级'
                }
              } else {
                console.warn('API返回失败:', classResponse)
                classIds = '获取班级信息失败'
              }
            } catch (classError) {
              console.error('获取班级信息失败:', classError)
              classIds = '获取班级信息失败'
            }
          }
        } catch (apiError) {
          console.warn('通过API获取用户信息失败，使用localStorage数据:', apiError)
          // 如果API调用失败，回退到localStorage数据
          studentId = localUserInfo.studentId || null
          classIds = localUserInfo.gradeId || null
        }

        this.debugInfo = {
          userId: userId,
          studentId: studentId,
          classIds: classIds
        }

        console.log('调试信息:', this.debugInfo)
      } catch (error) {
        console.error('获取调试信息失败:', error)
        this.debugInfo = {
          userId: '获取失败',
          studentId: '获取失败',
          classIds: '获取失败'
        }
      }
    },

    test1() {
      const imageUrl = 'http://localhost:8080/api/files/question-info/e47ee971-61e6-4d01-a8bc-62a5f5d2f257.jpg'
      localStorage.setItem('lastHomeworkImage', imageUrl)
      this.$message.success('图片URL已保存')
    },

    goToCourseSelection() {
      this.$router.push({ path: '/course/selection' })
    },

    getSavedImageUrl() {
      return localStorage.getItem('lastHomeworkImage')
    },

    testdownload() {
      const requestData = {
        upload_questions: {
          exam_title: '模拟考试试卷',
          student_info: {
            student_id: '',
            name: ''
          },
          question_data: [
            {
              type: '选择题',
              question: '以下哪个是正确的图标？',
              score: 5,
              options: [5, 6, 7, 8]
            },
            {
              type: '选择题',
              question: '1+1=?',
              score: 5,
              options: ['1', '2', '3', '4']
            },
            {
              type: '填空题',
              question: 'Python 中的列表用 ____ 表示。',
              score: 3
            },
            {
              type: '填空题',
              question: '2 + 3 = ____',
              score: 3
            },
            {
              type: '计算题',
              question: '计算 2 + 3 的结果。',
              score: 5
            },
            {
              type: '计算题',
              question: '计算 4 × 5 的结果。',
              score: 5
            },
            {
              type: '解答题',
              question: '简述 Python 中面向对象编程的概念。',
              score: 10
            },
            {
              type: '解答题',
              question: '解释什么是变量。',
              score: 10
            }
          ]
        }
      }

      console.log('发送的数据:', requestData)

      generateExam(requestData)
        .then(response => {
          console.log('生成试卷响应:', response)

          const blob = new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
          })

          const downloadUrl = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.style.display = 'none'
          link.href = downloadUrl
          link.download = '试卷.docx'

          document.body.appendChild(link)
          link.click()

          setTimeout(() => {
            document.body.removeChild(link)
            window.URL.revokeObjectURL(downloadUrl)
          }, 100)

          this.$message.success('试卷生成成功，开始下载')
        })
        .catch(error => {
          console.error('生成试卷失败:', error)
          if (error.response) {
            console.error('错误响应:', error.response)
          }
          this.$message.error('生成试卷失败，请稍后重试')
        })
    },

    test() {
      updateSubmissionStatus(6, 164, '已批改')
    },

    getHomeworkList() {
      console.log('getHomeworkList 开始执行')
      this.loading = true
      const token = getToken()
      if (!token) {
        console.error('未获取到登录信息')
        this.$message.error('未获取到登录信息')
        this.loading = false
        return
      }
      const userInfo = parseJwt(token)
      const userId = JSON.parse(userInfo.userInfo).id
      console.log('获取到的用户ID:', userId)

      if (!userId) {
        console.error('未获取到用户ID')
        this.$message.error('未获取到用户ID')
        this.loading = false
        return
      }

      console.log('开始调用 getHomeworkListByUserIdAndCourseId，参数:', { userId, courseId: this.courseId })
      // 使用用户ID和课程ID获取学生应该看到的作业列表
      getHomeworkListByUserIdAndCourseId(userId, this.courseId)
        .then(response => {
          console.log('学生课程作业列表响应:', response)
          if (response.code === 1) {
            this.homeworkList = response.data
            console.log('学生课程作业列表数据:', this.homeworkList)
            console.log('作业列表长度:', this.homeworkList.length)

            // 使用新的t_homework_submit表检查提交状态
            this.checkSubmissionStatus(userId)
          } else {
            console.error('获取课程作业列表失败，响应码:', response.code, '消息:', response.msg)
            this.loading = false
            this.$message.error(response.msg || '获取课程作业列表失败')
          }
        })
        .catch(error => {
          console.error('获取课程作业列表失败:', error)
          this.loading = false
          this.$message.error('获取课程作业列表失败，请稍后重试')
        })
    },

    async handleView(row) {
      this.currentHomework = row
      try {
        const response = await getSpecificHomeworkQuestions(row.id)
        if (response.code === 1) {
          // 定义题型优先级顺序
          const typeOrder = {
            1: 1, // 选择题
            2: 2, // 填空题
            3: 3, // 判断题
            4: 4 // 简答题
          }

          // 按题型排序题目
          this.homeworkDetail = response.data.sort((a, b) => {
            // 确保quType是数字类型
            const quTypeA = parseInt(a.quType) || a.quType
            const quTypeB = parseInt(b.quType) || b.quType

            const orderA = typeOrder[quTypeA] || 999
            const orderB = typeOrder[quTypeB] || 999

            if (orderA !== orderB) {
              return orderA - orderB
            }

            // 如果题型相同，按ID排序
            return a.id - b.id
          })

          // 如果是未提交作业，直接显示题目列表
          if (!row.submitted) {
            this.myAnswer = {}
            this.hasHomeworkImages = false
            this.homeworkImages = []
            this.answerDetails = []
            this.dialogVisible = true
            return
          }

          const token = getToken()
          const userInfo = parseJwt(token)
          const userId = JSON.parse(userInfo.userInfo).id

          // 通过用户ID查询学生信息，获取student_id
          const studentResponse = await fetch(`/api/student/user/${userId}`)
          const studentData = await studentResponse.json()

          if (studentData.code !== 1 || !studentData.data) {
            console.log('未找到学生信息，跳过加载作答详情')
            this.myAnswer = {}
            this.dialogVisible = true
            return
          }

          const studentId = studentData.data.studentId
          console.log('加载作答详情 - 通过用户ID查询到学生ID:', userId, '->', studentId)

          const res = await getSubmitsByStudentId(studentId)
          console.log('获取到的提交记录:', res)

          const submissions = res.data || []
          const submission = submissions.find(s => s.homeworkId === row.id)
          console.log('当前作业的提交记录:', submission)

          // 检查多个可能的图片字段
          const imageField = submission?.imageUrl || submission?.answerImageUrl
          console.log('提交记录字段检查:', {
            hasSubmission: !!submission,
            imageUrl: submission?.imageUrl,
            answerImageUrl: submission?.answerImageUrl,
            selectedField: imageField,
            submissionKeys: submission ? Object.keys(submission) : []
          })

          if (submission && imageField) {
            console.log('图片URL:', imageField)
            let imageUrl = imageField
            if (!imageUrl.startsWith('http')) {
              // MinIO文件直接通过MinIO API访问
              if (imageUrl.startsWith('/question-info/')) {
                imageUrl = `http://localhost:9000${imageUrl}`
              } else {
              imageUrl = `http://localhost:8080${imageUrl}`
              }
            }
            this.myAnswer = {
              ...submission,
              imageUrl: imageUrl
            }

            // 处理作业图片列表
            this.processHomeworkImages(imageField)
          } else {
            this.myAnswer = {}
            this.hasHomeworkImages = false
            this.homeworkImages = []
          }

          // 获取作答详情
          await this.loadAnswerDetails(studentId, row.id)

          this.dialogVisible = true
        } else {
          this.$message.error(response.msg || '获取作业详情失败')
        }
      } catch (error) {
        console.error('获取作业详情失败:', error)
        this.$message.error('获取作业详情失败，请稍后重试')
      }
    },

    async handleDownload(row) {
      // 先获取作业详情，然后生成文档
      this.currentHomework = row
      console.log('开始下载作业，作业ID:', row.id)

      try {
        // 获取学生信息
        let studentInfo = { student_id: '', name: '' }
        try {
          const studentResponse = await getStudentBindInfo()
          if (studentResponse.code === 1 && studentResponse.data) {
            studentInfo = {
              student_id: studentResponse.data.studentNum || '',
              name: studentResponse.data.name || ''
            }
            console.log('获取到学生信息:', studentInfo)
          } else {
            console.warn('获取学生信息失败，使用默认值')
          }
        } catch (error) {
          console.warn('获取学生信息异常，使用默认值:', error)
        }

        const response = await getSpecificHomeworkQuestions(row.id)
        console.log('获取作业详情响应:', response)

        if (response.code === 1) {
          this.homeworkDetail = response.data
          console.log('作业详情数据:', this.homeworkDetail)

          const questionTypeMap = {
            1: '选择题',
            2: '填空题',
            3: '判断题',
            4: '简答题'
          }

          // 获取题目详情
          const questionPromises = this.homeworkDetail.map(question =>
            getQuestionDetail(question.questionId)
              .then(detailResponse => {
                if (detailResponse.code === 1) {
                  return {
                    ...question,
                    ...detailResponse.data
                  }
                }
                return question
              })
              .catch(error => {
                console.error(`获取题目${question.questionId}详情失败:`, error)
                return question
              })
          )

          try {
            const updatedQuestions = await Promise.all(questionPromises)
            const questionData = updatedQuestions.map(q => ({
              type: questionTypeMap[q.quType] || '未知题型',
              question: q.content,
              score: q.point || 5
            }))

            console.log('准备下载的题目数据:', questionData)

            const requestData = {
              upload_questions: {
                exam_title: this.currentHomework?.name || '作业试卷',
                student_info: studentInfo,
                question_data: questionData
              }
            }

            console.log('发送给后端的请求数据:', requestData)

            const examResponse = await generateExam(requestData)
            const blob = new Blob([examResponse.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' })
            const downloadUrl = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.style.display = 'none'
            link.href = downloadUrl
            link.download = (this.currentHomework?.name || '作业') + '.docx'
            document.body.appendChild(link)
            link.click()
            setTimeout(() => {
              document.body.removeChild(link)
              window.URL.revokeObjectURL(downloadUrl)
            }, 100)
            this.$message.success('试卷生成成功，开始下载')
          } catch (error) {
            console.error('获取题目详情失败:', error)
            this.$message.warning('部分题目详情获取失败，使用基本信息生成文档')

            // 即使获取详情失败，也尝试用基本信息生成文档
            const questionData = this.homeworkDetail.map(q => ({
              type: questionTypeMap[q.quType] || '未知题型',
              question: q.content || '题目内容获取失败',
              score: q.point || 5
            }))

            const requestData = {
              upload_questions: {
                exam_title: this.currentHomework?.name || '作业试卷',
                student_info: studentInfo,
                question_data: questionData
              }
            }

            const examResponse = await generateExam(requestData)
            const blob = new Blob([examResponse.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' })
            const downloadUrl = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.style.display = 'none'
            link.href = downloadUrl
            link.download = (this.currentHomework?.name || '作业') + '.docx'
            document.body.appendChild(link)
            link.click()
            setTimeout(() => {
              document.body.removeChild(link)
              window.URL.revokeObjectURL(downloadUrl)
            }, 100)
            this.$message.success('试卷生成成功，开始下载')
          }
        } else {
          this.$message.error(response.msg || '获取作业详情失败')
        }
      } catch (error) {
        console.error('获取作业详情失败:', error)
        this.$message.error('获取作业详情失败，请稍后重试')
      }
    },

    // 检查作业提交状态
    async checkSubmissionStatus(userId) {
      try {
        // 通过用户ID查询学生信息，获取student_id
        const studentResponse = await fetch(`/api/student/user/${userId}`)
        const studentData = await studentResponse.json()

        if (studentData.code !== 1 || !studentData.data) {
          console.log('未找到学生信息，跳过提交状态检查')
          return
        }

        const studentId = studentData.data.studentId
        console.log('检查提交状态 - 通过用户ID查询到学生ID:', userId, '->', studentId)

        // 为每个作业检查提交状态
        const homeworkPromises = this.homeworkList.map(async (homework) => {
          try {
            const response = await getSubmitByStudentIdAndHomeworkId(parseInt(studentId), parseInt(homework.id))
            if (response.code === 1 && response.data) {
              // 检查提交状态：submitStatus === 1 表示已提交，submitStatus === 0 表示已退回
              if (response.data.submitStatus === 1) {
                // 已提交的作业，检查是否已批改
                const hasTeacherComment = response.data.teacherComment && response.data.teacherComment.trim() !== ''
                const hasScore = response.data.totalScore !== null && response.data.totalScore !== undefined && response.data.totalScore > 0
                const isGraded = hasTeacherComment || hasScore

              return {
                ...homework,
                submitted: true,
                  status: isGraded ? '已批改' : '未批改',
                  point: response.data.totalScore !== null && response.data.totalScore !== undefined ? Number(response.data.totalScore) : null,
                submitId: response.data.submitId
                }
              } else {
                // submitStatus === 0 表示已退回，应该显示为未提交
                return {
                  ...homework,
                  submitted: false,
                  status: null,
                  point: null
                }
              }
            } else {
              // 没有提交记录，标记为未提交
              return {
                ...homework,
                submitted: false,
                status: null,
                point: null
              }
            }
          } catch (error) {
            console.error(`检查作业${homework.id}提交状态失败:`, error)
            // 出错时默认为未提交
            return {
              ...homework,
              submitted: false,
              status: null,
              point: null
            }
          }
        })

        // 等待所有检查完成
        const updatedHomeworkList = await Promise.all(homeworkPromises)
        console.log('处理后的课程作业列表:', updatedHomeworkList)

        // 使用 Vue.set 或直接赋值来确保响应式更新
        this.homeworkList = updatedHomeworkList

        // 添加详细的调试信息
        this.homeworkList.forEach((homework, index) => {
          console.log(`作业${index + 1} (ID: ${homework.id}):`, {
            name: homework.name,
            submitted: homework.submitted,
            status: homework.status,
            point: homework.point,
            submitId: homework.submitId
          })
        })

        // 强制更新视图
        this.$forceUpdate()

        this.loading = false
        // 静默处理，避免页面初次进入即弹出成功提示
      } catch (error) {
        console.error('检查提交状态失败:', error)
        this.loading = false
        this.$message.error('检查提交状态失败')
      }
    },

    handleUpload(row) {
      this.currentHomework = row
      getSpecificHomeworkQuestions(row.id)
        .then(response => {
          if (response.code === 1) {
            this.homeworkDetail = response.data

            const questionPromises = this.homeworkDetail.map(question =>
              getQuestionDetail(question.questionId)
                .then(detailResponse => {
                  if (detailResponse.code === 1) {
                    return {
                      ...question,
                      ...detailResponse.data
                    }
                  }
                  return question
                })
                .catch(error => {
                  console.error(`获取题目${question.questionId}详情失败:`, error)
                  return question
                })
            )

            Promise.all(questionPromises)
              .then(updatedQuestions => {
                // 保存题目详情，用于后续添加错题本
                this.questionDetails = updatedQuestions
                this.transformedQuestions = updatedQuestions.map((question, index) => ({
                  stems: `${index + 1}.${question.content}`,
                  answer: question.analysis
                }))
                this.uploadDialogVisible = true
              })
              .catch(error => {
                console.error('获取题目详情失败:', error)
                this.$message.warning('部分题目详情获取失败，显示基本信息')
                this.uploadDialogVisible = true
              })
          } else {
            this.$message.error(response.msg || '获取作业详情失败')
          }
        })
        .catch(error => {
          console.error('获取作业详情失败:', error)
          this.$message.error('获取作业详情失败，请稍后重试')
        })
    },

    async submitUpload() {
      // 检查是否已选择作业和获取到用户ID
      if (!this.currentHomework || !this.currentHomework.id) {
        this.$message.error('未选择作业或作业ID无效')
        return
      }

      const token = getToken()
      if (!token) {
        this.$message.error('未获取到登录信息')
        return
      }
      const userInfo = parseJwt(token)
      const userId = JSON.parse(userInfo.userInfo).id

      if (!userId) {
        this.$message.error('未获取到用户ID')
        return
      }

      // 通过用户ID查询学生信息，获取student_id
      const studentResponse = await fetch(`/api/student/user/${userId}`)
      const studentData = await studentResponse.json()

      if (studentData.code !== 1 || !studentData.data) {
        this.$message.error('未找到学生信息，无法上传作业')
        return
      }

      const studentId = studentData.data.studentId
      console.log('上传作业 - 通过用户ID查询到学生ID:', userId, '->', studentId)

      if (this.fileList.length === 0) {
        this.$message.error('请选择要上传的文件')
        return
      }

      // 设置上传状态，隐藏上传弹窗
      this.uploading = true;
      this.uploadDialogVisible = false;

      // 显示处理中的加载消息
      let loadingMessage = this.$message({
        message: `正在处理 ${this.fileList.length} 张图片，请稍候...`,
        type: 'info',
        duration: 0,
        showClose: true
      })

      try {
        // 第一步：先上传所有图片到MinIO，收集所有图片URL
        const allImageUrls = [];
        for (let i = 0; i < this.fileList.length; i++) {
          const file = this.fileList[i];

          // 显示当前正在处理的图片信息
          this.$message({
              message: `正在上传第 ${i + 1} 张图片...`,
              type: 'info',
              duration: 1000
          });

          // 上传图片到 MinIO
          const uploadFormData = new FormData();
          uploadFormData.append('file', file.raw);
          const minioResponse = await uploadFile(uploadFormData);

          // 检查上传结果
          if (minioResponse.code !== 1) {
            console.error(`图片 ${i + 1} 上传失败:`, minioResponse.msg || '未知错误');
            this.$message.error(`图片 ${i + 1} 上传失败`);
            continue; // 当前图片处理失败，跳过后续步骤，继续处理下一张
          }

          // 收集图片URL
          const imageUrl = minioResponse.data || minioResponse.msg;
          allImageUrls.push(imageUrl);
        }

        // 第二步：如果有图片上传成功，提交到作业系统
        if (allImageUrls.length > 0) {
          // 如果有多张图片，将URL数组转换为JSON字符串
          const finalImageUrl = allImageUrls.length === 1 ? allImageUrls[0] : JSON.stringify(allImageUrls);

          // 使用第一张图片调用上传API（这会清除之前的图片并设置新的图片）
          const firstFile = this.fileList[0];
          const homeworkUploadFormData = new FormData();
          homeworkUploadFormData.append('file', firstFile.raw);
          homeworkUploadFormData.append('homeworkId', this.currentHomework.id);
          homeworkUploadFormData.append('studentId', studentId);

          const uploadResponse = await uploadHomeworkImage(homeworkUploadFormData);

          if (!uploadResponse || uploadResponse.code !== 1) {
             console.error('作业图片提交失败:', uploadResponse ? (uploadResponse.msg || '后端返回数据结构异常') : '后端未返回数据');
             this.$message.error('作业图片提交失败');
             return;
          }

          // 如果有多张图片，直接调用后端API更新图片URL
          if (allImageUrls.length > 1) {
            try {
              const response = await fetch('/api/homework-submit/update-image-url', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/x-www-form-urlencoded'
                },
                body: `homeworkId=${this.currentHomework.id}&studentId=${studentId}&imageUrls=${encodeURIComponent(finalImageUrl)}`
              });

              const result = await response.json();
              if (result.code !== 1) {
                console.error('多张图片URL更新失败:', result.msg || '未知错误');
                this.$message.error('多张图片URL更新失败');
                return;
              }
              console.log('多张图片URL更新成功:', finalImageUrl);
            } catch (error) {
              console.error('多张图片URL更新失败:', error);
              this.$message.error('多张图片URL更新失败');
              return;
            }
          }
        }

        // 图片上传完成，显示成功消息
        this.$message.success(`成功上传 ${allImageUrls.length} 张图片！`);

        // 刷新作业列表以更新状态显示
        this.getHomeworkList();
      } catch (error) {
        // 捕获处理图片过程中的任何未预期错误
        console.error('处理图片过程中发生错误:', error);
        this.$message.error('处理图片过程中发生错误，请稍后重试');
      } finally {
        // 最终都会执行的操作：取消上传状态，清空文件列表，并关闭 loadingMessage
        if (loadingMessage) loadingMessage.close();
        this.uploading = false;
        this.fileList = [];
      }
    },

    handleViewHomework(row) {
      console.log('点击作业名称，显示作业详情，作业ID:', row.id)
      // 直接调用 handleView 方法显示作业详情
      this.handleView(row)
    },

    // 处理作业图片列表
    processHomeworkImages(imageUrl) {
      try {
        console.log('processHomeworkImages 输入:', imageUrl)

        // 检查是否是JSON数组格式
        if (typeof imageUrl === 'string' && imageUrl.startsWith('[')) {
          const imageArray = JSON.parse(imageUrl)
          if (Array.isArray(imageArray)) {
            this.homeworkImages = imageArray.map(url => {
              if (!url.startsWith('http')) {
                // MinIO文件直接通过MinIO API访问
                if (url.startsWith('/question-info/')) {
                  return `http://localhost:9000${url}`
          } else {
                  return `http://localhost:8080${url}`
                }
              }
              return url
            })
            this.hasHomeworkImages = true
            this.currentImageIndex = 0
            console.log('处理后的图片数组:', this.homeworkImages)
            return
          }
        }

        // 单个图片URL
        if (imageUrl) {
          let processedUrl = imageUrl
          if (!imageUrl.startsWith('http')) {
            // MinIO文件直接通过MinIO API访问
            if (imageUrl.startsWith('/question-info/')) {
              processedUrl = `http://localhost:9000${imageUrl}`
            } else {
              processedUrl = `http://localhost:8080${imageUrl}`
            }
          }
          this.homeworkImages = [processedUrl]
          this.hasHomeworkImages = true
          this.currentImageIndex = 0
          console.log('处理后的单个图片URL:', processedUrl)
        } else {
          this.hasHomeworkImages = false
          this.homeworkImages = []
        }
      } catch (error) {
        console.error('处理作业图片失败:', error)
        this.hasHomeworkImages = false
        this.homeworkImages = []
      }
    },

    // 上一张图片
    prevImage() {
      if (this.currentImageIndex > 0) {
        this.currentImageIndex--
      }
    },

    // 下一张图片
    nextImage() {
      if (this.currentImageIndex < this.homeworkImages.length - 1) {
        this.currentImageIndex++
      }
    },

    // 选择指定图片
    selectImage(index) {
      if (index >= 0 && index < this.homeworkImages.length) {
        this.currentImageIndex = index
      }
    },

    // 处理图片加载错误
    handleImageError(event) {
      console.error('图片加载失败:', event.target.src)
      event.target.style.display = 'none'
    },

    // 弹窗关闭处理
    handleDialogClose() {
      this.dialogVisible = false
      this.hasHomeworkImages = false
      this.homeworkImages = []
      this.currentImageIndex = 0
    },

    // 弹窗打开处理
    handleDialogOpened() {
      // 添加键盘事件监听
      document.addEventListener('keydown', this.handleKeydown)
    },

    // 弹窗关闭后处理
    handleDialogClosed() {
      // 移除键盘事件监听
      document.removeEventListener('keydown', this.handleKeydown)
    },

    // 键盘事件处理
    handleKeydown(event) {
      if (!this.dialogVisible || !this.hasHomeworkImages) return

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault()
          this.prevImage()
          break
        case 'ArrowRight':
          event.preventDefault()
          this.nextImage()
          break
        case 'Escape':
          event.preventDefault()
          this.dialogVisible = false
          break
      }
    },

    // 在新标签页打开图片
    openImageInNewTab() {
      if (this.homeworkImages.length > 0) {
        window.open(this.homeworkImages[this.currentImageIndex], '_blank')
      }
    },

    // 退回作业
    async handleReturnHomework(row) {
      try {
        const confirm = await this.$confirm(
          `确定要退回作业"${row.name}"吗？退回后该作业将回到未提交状态，但之前的提交内容不会删除，再次提交时会覆盖之前的内容。`,
          '退回确认',
          {
            confirmButtonText: '确定退回',
            cancelButtonText: '取消',
            type: 'warning'
          }
        ).catch(() => false)

        if (!confirm) return

        // 获取学生信息
      const token = getToken()
      const userInfo = parseJwt(token)
      const userId = JSON.parse(userInfo.userInfo).id

        // 通过用户ID查询学生信息，获取student_id
        const studentResponse = await fetch(`/api/student/user/${userId}`)
        const studentData = await studentResponse.json()

        if (studentData.code !== 1 || !studentData.data) {
          this.$message.error('未找到学生信息')
          return
        }

        const studentId = studentData.data.studentId

        // 调用退回API
        const response = await returnHomework(studentId, row.id)
        console.log('退回作业API响应:', response)

        if (response.code === 1) {
          this.$message.success('作业退回成功')
          // 刷新作业列表
          this.getHomeworkList()
          } else {
          this.$message.error(response.msg || '退回作业失败')
        }
      } catch (error) {
        console.error('退回作业失败:', error)
        this.$message.error('退回作业失败，请稍后重试')
      }
    },

    handleMy(row) {
      const token = getToken()
      if (!token) {
        this.$message.error('未获取到登录信息')
        return
      }
      const userInfo = parseJwt(token)
      const studentId = JSON.parse(userInfo.userInfo).id
      if (!studentId) {
        this.$message.error('未获取到用户ID')
        return
      }
      getSubmitsByStudentId(studentId)
        .then(res => {
          const submissions = res.data || []
          if (submissions && Array.isArray(submissions)) {
            const submission = submissions.find(s => s.homeworkId === row.id)
            const imageField = submission?.imageUrl || submission?.answerImageUrl
            if (submission && imageField) {
              let url = imageField
              if (/^(blob:|https?:\/\/|\/)/.test(url)) {
                if (!/^https?:\/\//.test(url) && !/^blob:/.test(url)) {
                  url = 'http://localhost:8080' + url
                }
                this.myImageUrl = url
                this.myImageDialogVisible = true
              } else {
                this.$message.info('图片地址无效')
              }
            } else {
              this.$message.info('未上传图片')
            }
          } else {
            this.$message.info('未上传图片')
          }
        })
        .catch(() => {
          this.$message.error('获取图片信息失败')
        })
    },

    async addToWrongBook() {
      if (!this.finalErrorList || this.finalErrorList.length === 0) {
        this.$message.warning('没有可加入错题本的题目')
        return
      }
      try {
        for (const item of this.finalErrorList) {
          await addWrongQuestion({
            student_id: item.studentId,
            content: item.stem,
            analysis: item.comment || '',
            knowledge: item.knowledge || '',
            chapter: item.chapter || '',
            suboptions: '数学',
            answer: item.answer || ''
          })
        }
        this.$message.success('已加入错题本')
      } catch (e) {
        this.$message.error('加入错题本失败')
        console.error(e)
      }
    },

    getGradingStatusType(status) {
      if (!status) return 'info'
      return status === '已批改' ? 'success' : 'warning'
    },
    getGradingStatusText(status) {
      if (!status) return '未提交'
      return status === '已批改' ? '已批改' : '未批改'
    },
    goToUpload() {
      this.$router.push({ name: 'UploadComponent' })
    },
    goToPrint() {
      this.$router.push({ name: 'PrintComponent' })
    },
    // 计算提交率
    getSubmissionRate(homeworkId) {
      if (this.totalStudents === 0) return '0%'

      const submittedCount = this.submissions.filter(
        submission => submission.homeworkId === homeworkId && submission.submitted
      ).length

      const rate = (submittedCount / this.totalStudents) * 100
      return rate.toFixed(1) + '%'
    },
    // 解码Unicode字符串
    decodeUnicode(str) {
      if (!str) return ''
      return decodeURIComponent(escape(str))
    },
    handleImageError(event) {
      console.error('图片加载失败:', event)
      this.$message.error('图片加载失败，请检查网络连接')
    },

    // 加载作答详情
    async loadAnswerDetails(studentId, homeworkId) {
      try {
        // 获取学生的作业提交记录
        const submitResponse = await getSubmitByStudentIdAndHomeworkId(studentId, homeworkId)
        console.log('获取提交记录响应:', submitResponse)

        if (submitResponse.code === 1 && submitResponse.data) {
          const submitRecord = submitResponse.data
          console.log('找到提交记录:', submitRecord)

          // 获取提交明细
          const detailsResponse = await getSubmitDetails(submitRecord.submitId)
          console.log('获取提交明细响应:', detailsResponse)

          if (detailsResponse.code === 1 && detailsResponse.data) {
            const details = detailsResponse.data
            console.log('提交明细:', details)

            // 处理作答详情，按照homeworkDetail的顺序排序
            this.answerDetails = this.homeworkDetail.map(question => {
              const detail = details.find(d => d.questionId === (question.questionId || question.id))
              if (detail) {
                return {
                  ...detail,
                  questionType: question.quType
                }
              } else {
                // 即使没有作答记录，也创建一个空记录用于显示
                return {
                  questionId: question.questionId || question.id,
                  answerText: '',
                  answerImageUrl: '',
                  score: 0,
                  teacherComment: '',
                  isCorrect: null,
                  questionType: question.quType
                }
              }
            })

            console.log('处理后的作答详情:', this.answerDetails)
            console.log('作答详情数量:', this.answerDetails.length)

            // 检查每个答案的详情
            this.answerDetails.forEach((detail, index) => {
              console.log(`题目${index + 1} (ID: ${detail.questionId}):`, {
                answerText: detail.answerText,
                answerImageUrl: detail.answerImageUrl,
                hasAnswer: !!(detail.answerText || detail.answerImageUrl)
              })
            })
          } else {
            this.answerDetails = []
          }
        } else {
          this.answerDetails = []
        }
      } catch (error) {
        console.error('加载作答详情失败:', error)
        this.answerDetails = []
      }
    },

    // 获取题目类型名称
    getQuestionTypeName(quType) {
      const typeMap = {
        1: '选择题',
        2: '填空题',
        3: '判断题',
        4: '简答题'
      }
      return typeMap[quType] || '未知题型'
    },

    // 获取题目内容
    getQuestionContent(questionId) {
      const question = this.homeworkDetail.find(q => (q.questionId || q.id) === questionId)
      return question ? question.content : '题目内容获取失败'
    },

    // 获取题目在homeworkDetail中的索引
    getQuestionIndex(questionId) {
      return this.homeworkDetail.findIndex(q => (q.questionId || q.id) === questionId)
    },
    handleFileChange(file, fileList) {
      this.fileList = fileList
    },

    handlePreview(file) {
      console.log('预览文件:', file)
    },

    handleRemove(file, fileList) {
      console.log('移除文件:', file, fileList)
    },

    beforeRemove(file, fileList) {
      return this.$confirm(`确定移除 ${file.name}？`)
    },

    handleExceed(files, fileList) {
      this.$message.warning(`当前限制选择 3 个文件，本次选择了 ${files.length} 个文件，共选择了 ${files.length + fileList.length} 个文件`)
    },

    getSubmissionPoint(homework) {
      return homework.point;
    },
    // 处理科目变化
    handleSubjectChange(value) {
      this.selectedSubject = value
      // 可以在这里添加其他处理逻辑，比如重新获取数据等
    },

    // 在线答题
    handleOnlineExam(row) {
      this.$router.push({
        name: 'OnlineExam',
        params: {
          homeworkId: row.id
        },
        query: {
          name: row.name,
          courseId: this.courseId // 传递课程ID
        }
      })
    },

    // 重新上传作业图片
    handleReuploadImages(row) {
      this.currentHomework = row
      this.uploadDialogVisible = true
    }
  }
}
</script>

<style scoped>
.app-container {
  padding: 20px;
}
.el-tabs {
  margin-top: 20px;
}

.empty-homework-card {
  margin-bottom: 30px;
  text-align: center;
}

.homework-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.homework-header span {
  font-size: 18px;
  font-weight: bold;
  color: #409EFF;
}

.summary-header {
  margin-bottom: 20px;
}

.summary-header h3 {
  margin: 0 0 10px 0;
  color: #303133;
  font-size: 20px;
  font-weight: bold;
}

.debug-info {
  margin-top: 10px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.debug-info .el-tag {
  margin: 0;
}

.empty-content {
  padding: 40px 20px;
}

.empty-icon {
  font-size: 80px;
  color: #c0c4cc;
  margin-bottom: 20px;
}

.empty-text h3 {
  color: #606266;
  margin-bottom: 10px;
}

.empty-text p {
  color: #909399;
  margin-bottom: 30px;
}

.empty-actions {
  display: flex;
  justify-content: center;
  gap: 20px;
}
</style>
